/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useCallback, useMemo, useState } from 'react'
import { Modal, Alert } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import Button from '../ts-components/Button'
import useWindowSize from 'src/hooks/useWindowSize'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`

const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10002,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

interface ReportModalProps {
  isOpen: boolean
  onClose: () => void
  onReport: () => void
  entityType?: 'post' | 'message' | 'user'
  entityId?: string
  title?: string
}

/**
 * ReportModal component for reporting content in the web interface
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onReport - Function to handle the report action
 * @param {string} props.entityType - Type of entity being reported (post, message, user, etc.)
 * @param {string} props.entityId - ID of the entity being reported
 * @param {string} props.title - Custom title for the modal
 * @returns {JSX.Element} ReportModal component
 */
const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  onReport,
  title = 'Report',
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleReport = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // TODO: Implement actual API call here
      await onReport()

      onClose()
    } catch (e: any) {
      setError(e?.message || 'Failed to submit report')
    } finally {
      setIsLoading(false)
    }
  }, [onReport, onClose])

  const handleCancel = useCallback(() => {
    setError(null)
    onClose()
  }, [onClose])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button text='Cancel' onClick={handleCancel} variant='secondary' />
        <Button
          text={isLoading ? 'Submitting...' : 'Report'}
          onClick={handleReport}
          variant='danger'
          disabled={isLoading}
        />
      </div>
    )
  }, [isMobile, handleCancel, handleReport, isLoading])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {title}
      </Text>
    )
  }, [title])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        /* background: ${colors.SECONDARY_400} !important; */
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '30vw'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
          background: ${colors.SECONDARY_300};
        }
      `}
      width={isMobile ? '100%' : '40vw'}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={handleCancel}
      centered={!isMobile}
      maskClosable={false}
      classNames={classNames}
      styles={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 24px;
          padding: 16px 8px;
          flex: 1;
        `}
      >
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.WHITE}
          style={`text-align: center;`}
        >
          This report will be sent to SportNinja.
          <br />
          The person won&apos;t know who reported them.
        </Text>

        {error && <Alert message={error} type='error' showIcon />}
      </div>
    </Modal>
  )
}

export default ReportModal
